import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchAllDeals, searchDeals, loadMoreDeals, clearErrors, clearLoadMoreError,
  setCategory, setSort, setActiveFilters,
} from '../slices/dealSlice';

const PAGE_SIZE = 50;

/**
 * Deal-list state + actions for the homepage grid.
 *
 * Every action goes through loadDeals(), which always sends the full, current
 * set of parameters (query, category, sort, tag pills). That is what keeps the
 * header, the pills and the results describing the same list.
 *
 * @param {object} [initial]
 * @param {string} [initial.searchQuery] - from ?search= (e.g. the product page search bar)
 * @param {string} [initial.category]    - from ?category= (e.g. the product page dropdown)
 */
export const useDeals = ({ searchQuery: initialSearchQuery, category: initialCategory } = {}) => {
  const dispatch = useDispatch();
  const {
    list: deals,
    loading,
    loadingMore,
    error,
    loadMoreError,
    currentSearchTerm,
    currentCategory,
    currentSort,
    activeFilters,
    pagination,
    listRequestId,
  } = useSelector((state) => state.deals);

  /** Replace the list. Omitted params keep their current value. */
  const loadDeals = ({
    query = currentSearchTerm,
    category = currentCategory,
    sort = currentSort,
    tags = activeFilters,
  } = {}) => {
    const params = { page: 1, limit: PAGE_SIZE, category, sort, tags };
    if (query && query.trim() !== '') {
      dispatch(searchDeals({ ...params, query: query.trim() }));
    } else {
      dispatch(fetchAllDeals(params));
    }
  };

  useEffect(() => {
    if (initialSearchQuery && initialSearchQuery.trim()) {
      // A search started from another page is a fresh, site-wide search
      dispatch(setCategory('all'));
      loadDeals({ query: initialSearchQuery, category: 'all' });
    } else if (initialCategory && initialCategory.trim()) {
      dispatch(setCategory(initialCategory));
      loadDeals({ query: '', category: initialCategory });
    } else if (deals.length === 0 && !loading && !currentSearchTerm) {
      loadDeals();
    }
    // Initial load only: later changes go through the actions below, so re-running
    // this on state changes would double-fetch.
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const search = (query, category = currentCategory) => loadDeals({ query: query || '', category });

  const filterByCategory = (category) => {
    dispatch(setCategory(category));
    loadDeals({ category });
  };

  const sortBy = (sortValue) => {
    dispatch(setSort(sortValue));
    loadDeals({ sort: sortValue });
  };

  const filterByTags = (filters) => {
    dispatch(setActiveFilters(filters));
    loadDeals({ tags: filters });
  };

  const loadMore = () => {
    if (pagination.hasNextPage && !loadingMore) {
      dispatch(loadMoreDeals({
        page: pagination.nextPage,
        limit: pagination.limit,
        searchQuery: currentSearchTerm,
        category: currentCategory,
        sort: currentSort,
        tags: activeFilters,
        listRequestId, // lets the reducer drop this page if the list is replaced meanwhile
      }));
    }
  };

  const refetch = () => loadDeals();
  const clearSearch = () => loadDeals({ query: '' });

  // Reset category + tag filters (keeps the current sort) and reload the default feed
  const clearFilters = () => {
    dispatch(setActiveFilters([]));
    dispatch(setCategory('all'));
    loadDeals({ query: '', category: 'all', tags: [] });
  };

  return {
    deals,
    loading,
    loadingMore,
    error,
    loadMoreError,
    currentSearchTerm,
    currentCategory,
    currentSort,
    activeFilters,
    pagination,
    refetch,
    search,
    filterByCategory,
    filterByTags,
    sortBy,
    loadMore,
    clearError: () => dispatch(clearErrors()),
    clearLoadMoreError: () => dispatch(clearLoadMoreError()),
    clearSearch,
    clearFilters,
  };
};

export default useDeals;
